import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  QueryStateBoundary,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import {
  useGenerateInvoicePdf,
  useInvoice,
  usePayments,
  useSendInvoice,
  useVoidInvoice,
} from "@/api/hooks/useInvoices";
import { useEvent } from "@/api/hooks/useEvents";
import {
  INVOICE_KIND_BADGE_VARIANT,
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
  paidCents,
  type Invoice,
} from "@/api/types/invoice";
import { PAYMENT_METHOD_LABELS, type Payment } from "@/api/types/payment";
import { formatShortDate } from "@/features/events/eventDate";
import { RecordPaymentDialog } from "./RecordPaymentDialog";

/**
 * `payment.received_at` is a full timestamp (like quote.ts's sent_at /
 * accepted_at), not a plain "YYYY-MM-DD" date — eventDate.ts's
 * `formatShortDate` deliberately splits on "-" for LOCAL date-only parsing
 * and would choke on a trailing "T..." time component, so timestamps get
 * their own formatter here (mirrors QuoteDetailPage's formatTimestamp).
 */
function formatPaymentTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: invoice, isLoading, error, refetch } = useInvoice(id);
  const { data: event } = useEvent(invoice?.event_id);
  const { data: payments } = usePayments(id);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [pdfHref, setPdfHref] = useState<string | null>(null);

  const sendInvoice = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const generatePdf = useGenerateInvoicePdf();

  async function handleSend() {
    if (!invoice) return;
    try {
      await sendInvoice.mutateAsync({ id: invoice.id });
      toastSuccess("Invoice sent");
    } catch (err) {
      toastError("Couldn't send invoice", getErrorMessage(err));
    }
  }

  async function handleVoid() {
    if (!invoice) return;
    try {
      await voidInvoice.mutateAsync({ id: invoice.id });
      toastSuccess("Invoice voided");
    } catch (err) {
      toastError("Couldn't void invoice", getErrorMessage(err));
    }
  }

  async function handleGeneratePdf() {
    if (!invoice) return;
    try {
      const result = await generatePdf.mutateAsync(invoice.id);
      // Contract: "return {pdf_key} or {url}" — prefer an explicit url, fall
      // back to a GET on the invoice's own path (mirrors the menu/shopping
      // list PDF convention from Phase 4).
      setPdfHref(result.url ?? `/api/v2/invoices/${invoice.id}/pdf`);
      toastSuccess("Invoice PDF ready");
    } catch (err) {
      toastError("Couldn't generate invoice PDF", getErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 sm:p-8">
      <Button variant="link" size="sm" onClick={() => navigate("/money")}>
        ← Back to money
      </Button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {invoice && (
          <>
            <InvoiceDetailCard
              invoice={invoice}
              eventDate={event?.event_date ?? null}
              eventVenue={event?.venue_name ?? null}
              onSend={() => void handleSend()}
              onVoid={() => void handleVoid()}
              onGeneratePdf={() => void handleGeneratePdf()}
              onRecordPayment={() => setPaymentOpen(true)}
              isSending={sendInvoice.isPending}
              isVoiding={voidInvoice.isPending}
              isGeneratingPdf={generatePdf.isPending}
              pdfHref={pdfHref}
            />

            <PaymentsCard payments={payments ?? []} />

            <RecordPaymentDialog
              open={paymentOpen}
              onClose={() => setPaymentOpen(false)}
              invoiceId={invoice.id}
            />
          </>
        )}
      </QueryStateBoundary>
    </div>
  );
}

interface InvoiceDetailCardProps {
  invoice: Invoice;
  eventDate: string | null;
  eventVenue: string | null;
  onSend: () => void;
  onVoid: () => void;
  onGeneratePdf: () => void;
  onRecordPayment: () => void;
  isSending: boolean;
  isVoiding: boolean;
  isGeneratingPdf: boolean;
  pdfHref: string | null;
}

function InvoiceDetailCard({
  invoice,
  eventDate,
  eventVenue,
  onSend,
  onVoid,
  onGeneratePdf,
  onRecordPayment,
  isSending,
  isVoiding,
  isGeneratingPdf,
  pdfHref,
}: InvoiceDetailCardProps) {
  // Status gating: Send only from draft; Void blocked once fully paid (a
  // paid invoice being voided is the skeptic brief's named attack — the
  // action simply isn't offered once status === "paid"); Void is also
  // pointless once already void. Record-payment is offered for anything
  // still collectible.
  const canSend = invoice.status === "draft";
  const canVoid = invoice.status !== "paid" && invoice.status !== "void";
  const canRecordPayment = invoice.status !== "void" && invoice.status !== "paid";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{invoice.number}</CardTitle>
          {eventDate && (
            <p className="text-sm text-text-secondary">
              <Link to={`/events/${invoice.event_id}`} className="text-primary hover:underline">
                {formatShortDate(eventDate)}
                {eventVenue ? ` — ${eventVenue}` : ""}
              </Link>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={INVOICE_KIND_BADGE_VARIANT[invoice.kind]}>
            {INVOICE_KIND_LABELS[invoice.kind]}
          </Badge>
          <Badge variant={INVOICE_STATUS_BADGE_VARIANT[invoice.status]} data-testid="invoice-status-badge">
            {INVOICE_STATUS_LABELS[invoice.status]}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-text-primary">Line items</h3>
          <div className="divide-y divide-border rounded-md border border-border">
            {invoice.line_items.map((li, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-text-primary">
                  {li.label}
                  <span className="ml-2 text-text-muted">× {li.qty}</span>
                </span>
                <span className="text-text-secondary">{centsToUsd(li.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>

        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-text-secondary">Subtotal</dt>
            <dd className="font-medium text-text-primary">{centsToUsd(invoice.subtotal_cents)}</dd>
          </div>
          <div className="flex justify-between border-t border-border pt-1">
            <dt className="font-semibold text-text-primary">Total</dt>
            <dd className="font-semibold text-text-primary" data-testid="invoice-total">
              {centsToUsd(invoice.total_cents)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-text-secondary">Paid</dt>
            <dd className="font-medium text-text-primary" data-testid="invoice-paid">
              {centsToUsd(paidCents(invoice))}
            </dd>
          </div>
          {invoice.due_date && (
            <div className="flex justify-between">
              <dt className="text-text-secondary">Due</dt>
              <dd className="font-medium text-text-primary">{formatShortDate(invoice.due_date)}</dd>
            </div>
          )}
        </dl>

        {invoice.notes && <p className="text-sm text-text-secondary">{invoice.notes}</p>}

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          {pdfHref ? (
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-primary hover:underline"
              data-testid="invoice-pdf-link"
            >
              Open PDF
            </a>
          ) : invoice.pdf_key ? (
            <a
              href={`/api/v2/invoices/${invoice.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-primary hover:underline"
            >
              Download PDF
            </a>
          ) : (
            <Button variant="secondary" size="sm" onClick={onGeneratePdf} disabled={isGeneratingPdf}>
              {isGeneratingPdf ? "Preparing..." : "Generate PDF"}
            </Button>
          )}

          <div className="ml-auto flex flex-wrap gap-2">
            {canRecordPayment && (
              <Button size="sm" onClick={onRecordPayment}>
                Record payment
              </Button>
            )}
            {canSend && (
              <Button variant="secondary" size="sm" onClick={onSend} disabled={isSending}>
                {isSending ? "Sending..." : "Send"}
              </Button>
            )}
            {canVoid && (
              <Button variant="danger" size="sm" onClick={onVoid} disabled={isVoiding}>
                {isVoiding ? "Voiding..." : "Void"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentsCard({ payments }: { payments: Payment[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payments</CardTitle>
      </CardHeader>
      <CardContent>
        {payments.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="payments-empty">
            No payments recorded yet.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="payments-list">
            {payments.map((payment) => (
              <li key={payment.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-text-primary">
                    {PAYMENT_METHOD_LABELS[payment.method]}
                    {payment.reference && (
                      <span className="ml-2 text-xs text-text-muted">{payment.reference}</span>
                    )}
                  </p>
                  <p className="text-xs text-text-muted">{formatPaymentTimestamp(payment.received_at)}</p>
                </div>
                <span className="font-medium text-text-primary">{centsToUsd(payment.amount_cents)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
